import type { ReactNode } from "react";
import { cn } from "@/lib/utils/cn";

export type ChipAccent = "you" | "partner" | "shared" | "paper-deep" | "ink-1" | "ink-2" | "gold";

export type ChipKind = "category" | "status";

export interface FilterChipItem {
  id: string;
  label: string;
  /** Optional icon (lucide-react element). status + icon triggers dev warn. */
  icon?: ReactNode;
}

export interface FilterChipGroupProps {
  items: FilterChipItem[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  accent?: ChipAccent;
  kind?: ChipKind;
  className?: string;
  ariaLabel?: string;
  /**
   * Mobile layout (per MOBILE-UX-goal-prompt.md acceptance #3):
   *   - "wrap"  → flex-wrap on all viewports (default; desktop behaviour)
   *   - "swipe" → horizontal swipeable rail on mobile, flex-wrap on ≥ md
   * Wrap pages that have ≤ 4 chips should keep "wrap"; filter rails with
   * ≥ 5 chips switch to "swipe".
   */
  layout?: "wrap" | "swipe";
}

/**
 * W56.0 — the selected state is a **shape**, not a solid fill.
 *
 * It used to be a near-black solid (`bg-ink-1 text-paper`). Measured, that
 * fill is 16.31:1 against the unselected surface — the loudest thing on the
 * screen, and on a filter rail it outweighed the primary button standing
 * beside it.
 *
 * The replacement candidates (tint backgrounds) all measured 1.09–1.14:1
 * against `bg-paper-elevated`: visually indistinguishable from an unselected
 * chip. So the tint cannot carry the selection on its own, and the signal is
 * split three ways:
 *
 *   - background — the accent's tint (colour, deliberately weak)
 *   - border     — `border-hairline` → `border-hairline-strong` (a step)
 *   - dot        — a 6px accent-coloured dot (shape + colour)
 *
 * The dot is what makes the selection legible, and the border is what makes
 * it legible without colour — DESIGN §15 requires a non-colour signal paired
 * with the colour one, and the project's standing rule is that colour is
 * never the only signal.
 */
const accentToSelectedBg: Record<ChipAccent, string> = {
  you: "bg-you-tint",
  partner: "bg-partner-tint",
  shared: "bg-shared-tint",
  "paper-deep": "bg-paper-deep",
  // `ink-1` / `ink-2` are channel-form, so the opacity modifier works. They
  // are the *neutral* filters (a diary scope rail, a date filter), so they
  // get a neutral tint rather than a brand one.
  //
  // The alpha is written as `[0.07]` rather than `/7` because Tailwind's
  // opacity scale has no `7`: `/7` is not a scale step, so Tailwind generates
  // **no rule for it** and the chip would render with no background at all —
  // the same 「class name exists, CSS does not」 defect the hard-rule audit's
  // #17c check exists to catch. `/[0.07]` is the same 7% and does emit.
  "ink-1": "bg-ink-1/[0.07]",
  "ink-2": "bg-ink-2/[0.07]",
  gold: "bg-gold-tint",
};

/**
 * The dot's colour.
 *
 * A *surface* accent cannot paint the dot — the dot needs a colour that reads
 * against its own tint — so `paper-deep` falls back to the neutral ink.
 *
 * `gold-ink`, not `gold`: `gold` measures 3.25:1 on white and 2.72:1 on its
 * own tint, and a 6px dot has to clear 3:1.
 */
const accentToDot: Record<ChipAccent, string> = {
  you: "bg-you-strong",
  partner: "bg-partner-strong",
  shared: "bg-shared-strong",
  "paper-deep": "bg-ink-1",
  "ink-1": "bg-ink-1",
  "ink-2": "bg-ink-2",
  gold: "bg-gold-ink",
};

/**
 * P7 — FilterChipGroup.
 *
 * Per `component-api.md §2 P7` + `§6`:
 *   - pill chip + 7-accent taxonomy
 *   - 驱动: `data-accent` + `aria-checked` (never inline style)
 *   - 7 accent: you / partner / shared / paper-deep / ink-1 / ink-2 / gold
 *   - kind `'category' | 'status'` (status + icon triggers dev-mode console.warn)
 *   - `layout="swipe"`: mobile horizontal swipeable rail + desktop wrap
 *     (per MOBILE-UX-goal-prompt.md acceptance #3)
 *
 * `role="radio"` carries the selection through `aria-checked`. It used to also
 * declare `aria-pressed`, which is not an allowed attribute on a radio — the
 * two are different widgets' states, and a screen reader reading both would
 * report a radio as a toggle button.
 */
export function FilterChipGroup({
  items,
  selectedId,
  onSelect,
  accent = "you",
  kind = "category",
  className,
  ariaLabel,
  layout = "wrap",
}: FilterChipGroupProps) {
  // Per spec: status + icon is an anti-pattern. Log warning once per group.
  if (kind === "status" && items.some((i) => i.icon)) {
    console.warn(
      "[FilterChipGroup] kind='status' + icon triggers visual noise — prefer category accent.",
    );
  }

  // The container switches layout via responsive classes. On mobile (swipe)
  // the rail scrolls horizontally with snap; on ≥ md it wraps to fill.
  //
  // Per W35.5 follow-up issue #7: when the chip count exceeds the visible
  // width, the swipe rail is scrollable but the user has no visual cue
  // that the cut-off chip exists. `mask-image` paints a fade-out gradient
  // on the trailing edge (and leading edge when scrolled) so the user
  // sees "there's more →".
  const containerClass = cn(
    layout === "swipe"
      ? // mobile: horizontal scroll + snap + hidden scrollbar + fade-out
        // desktop: switch to flex-wrap
        "flex snap-x snap-mandatory gap-2 overflow-x-auto scroll-smooth " +
          "[-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden " +
          "mask-[linear-gradient(to_right,transparent_0,black_24px,black_calc(100%-24px),transparent_100%)] " +
          "md:flex-wrap md:overflow-visible md:snap-none md:mask-none"
      : "flex flex-wrap gap-2",
    className,
  );

  // On swipe layout the first/last chip should align with the page gutter;
  // bleed the inner padding so they line up flush.
  const itemBleed =
    layout === "swipe" ? "first:ml-0 last:mr-0 snap-start" : "";

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      data-component="FilterChipGroup"
      data-accent={accent}
      data-kind={kind}
      data-layout={layout}
      className={containerClass}
    >
      {items.map((item) => {
        const isSelected = item.id === selectedId;
        return (
          <button
            key={item.id}
            type="button"
            role="radio"
            aria-checked={isSelected}
            data-accent={accent}
            onClick={() => onSelect(item.id)}
            className={cn(
              "tap-target press inline-flex items-center gap-1.5 shrink-0",
              "h-11 px-4 rounded-pill text-meta",
              "border transition-colors duration-quick ease-paper",
              isSelected
                ? cn(accentToSelectedBg[accent], "border-hairline-strong text-ink-1")
                : "bg-paper-elevated text-ink-1 border-hairline hover:bg-black/5",
              itemBleed,
            )}
          >
            {/* The dot's slot is occupied in BOTH states — only its opacity
                changes. That is load-bearing, not cosmetic: a chip whose width
                changes when the selection moves makes a swipe rail's scroll
                position drift under the user's finger. It is therefore always
                rendered, never conditionally. */}
            <span
              aria-hidden
              data-component="FilterChipGroupDot"
              className={cn(
                "h-1.5 w-1.5 shrink-0 rounded-full",
                accentToDot[accent],
                isSelected ? "opacity-100" : "opacity-0",
              )}
            />
            {item.icon ? (
              <span aria-hidden className="icon-sm">
                {item.icon}
              </span>
            ) : null}
            <span>{item.label}</span>
          </button>
        );
      })}
    </div>
  );
}
